"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ToastAction } from "@/components/ui/toast";
import { toast } from "@/components/ui/use-toast";
import { redirectWithEmail } from "@/lib/helpers/redirect-with-email";
import { fetchWrapper } from "@/lib/hooks/fetch-client";
import { cn } from "@/lib/utils";

import ExamineLogo from "./icons/examine-logo";
import { Checkbox } from "./ui/checkbox";

interface NewsletterOptin {
	children: React.ReactNode;
}

const formSchema = z.object({
	email: z.string().email({ message: "Please enter a valid email address" }),
	form_id: z.string(),
	check: z.boolean(),
});

export default function NewsletterOptin({ children }: NewsletterOptin) {
	const [loading, setLoading] = React.useState(false);

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: "",
			form_id: "3671730",
			check: false,
		},
	});

	async function onSubmit(values: z.infer<typeof formSchema>) {
		setLoading(true);
		const payload = {
			email: values.email,
			form_id: values?.check ? "3668611" : "3671730",
		};

		await fetchWrapper("/v1/forms/newsletter/subscribe", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		})
			.then(() => redirectWithEmail("/insiders/welcome/", values.email))
			.catch((error: any) => {
				toast({
					title: "Error!",
					description: error.message,
					variant: "error",
				});
			})
			.finally(() => setLoading(false));
	}

	return (
		<Dialog>
			<DialogTrigger>{children}</DialogTrigger>
			<DialogContent
				className={cn(
					"max-w-[50rem] justify-center rounded-lg border-2 border-primary bg-white p-8"
				)}
			>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<ExamineLogo className="mb-6 h-4 w-20 sm:h-7 sm:w-28" />
						<h2 className="mb-3 font-lora text-xl font-medium leading-normal text-primary sm:text-36">
							Evidence-based health information you can trust
						</h2>
						<p className="mb-8 text-sm leading-normal sm:text-18">
							We analyze new studies every day. Get accurate and up-to-date insights to help you
							train better, eat healthier, and supplement smarter.
						</p>
						<div className="mb-8">
							<div className="mb-2 flex flex-wrap items-start gap-2 sm:flex-nowrap">
								<FormField
									control={form.control}
									name="email"
									render={({ field }) => (
										<FormItem className="w-full sm:w-[70%]">
											<FormControl>
												<Input placeholder="Your email address" className="py-3" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<Button type="submit" className="w-full justify-center py-3 sm:w-[30%]">
									Subscribe
								</Button>
							</div>
						</div>
						<div className="mb-2">
							<h4 className="mb-2 font-lora text-sm font-medium sm:text-2xl">
								Learn how to figure out what supplements work:
							</h4>
							<div className="flex items-start gap-2 text-xs leading-6 sm:text-base">
								<FormField
									control={form.control}
									name="check"
									render={({ field }) => (
										<FormItem className="flex items-baseline gap-2 space-y-0">
											<FormControl>
												<Checkbox checked={field.value} onCheckedChange={field.onChange} />
											</FormControl>
											<FormLabel>
												Sign up for tips on supplement safety, quality, strategies, and more with
												our 5-day mini course.
											</FormLabel>
										</FormItem>
									)}
								/>
							</div>
						</div>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}
